import { groupBy, uniq } from "~/utils/tsUtils"
import React from "react"

import { Badge } from "~/components/reusable/Badge"
import { TagBadgeWithRemove, type Tag } from "~/components/reusable/TagBadgeWithRemove"
import { ConfirmationDialog } from "~/components/reusable/ConfirmationDialog"
import { getEntriesWithTag, useTagActions, type TaggableVariant } from "~/hooks/useTagActions"
import { cn } from "~/utils/helpers"

export type { Tag }
export type { TaggableVariant }

type Props = {
  tags: Tag[]
  entries: TaggableVariant[]
  count?: number
  inTable?: boolean
  removable?: boolean
  filtreable?: boolean
}

export default function TagBadge({
  tags,
  entries,
  count,
  inTable,
  removable = true,
  filtreable = true,
}: Props) {
  const skuIds = entries.flatMap((row) => row.skuIds)

  const { tagIsFiltered, filterOnClick, onRemoveTag, selectedTag, setSelectedTag, canEdit } =
    useTagActions({ skuIds })

  // Removal is scoped to the variants that actually carry the selected tag, not every
  // variant in the (possibly grouped) cell.
  const selectedTagEntries = getEntriesWithTag(entries, selectedTag)
  const selectedTagSkuIds = uniq(selectedTagEntries.flatMap((entry) => entry.skuIds))

  const entriesCount = entries.length
  const tagGroups = entries.flatMap((entry) => entry.tags)
  const groupedTags = groupBy(tagGroups, (tag) => tag.id)

  const MAX_DISPLAYED_REMAINING = 9

  const tagCount = count ?? 5
  const sortedTags = [...tags].sort((a, b) => a.name.localeCompare(b.name))
  const remainingCount = tags.length - tagCount

  const handleFilterClick = (tag: Tag) => {
    if (filtreable) {
      filterOnClick(tag)
    }
  }

  return (
    <>
      <div className={cn("flex items-center", !inTable && "max-w-96")}>
        {sortedTags.slice(0, tagCount).map((tag) => {
          const groupedTagCount = groupedTags?.[tag.id]?.length
          return (
            <div key={tag.id}>
              {removable ? (
                <TagBadgeWithRemove
                  tag={tag}
                  isActive={tagIsFiltered(tag)}
                  canEdit={canEdit}
                  onFilterClick={handleFilterClick}
                  onRemove={setSelectedTag}
                  className={cn({
                    "max-w-[100px]": inTable,
                    "mr-0.5": groupedTagCount && groupedTagCount < entriesCount,
                  })}
                />
              ) : (
                <Badge
                  variant="tag"
                  isActive={tagIsFiltered(tag)}
                  className={cn("max-h-[22px] cursor-pointer", {
                    "max-w-[100px]": inTable,
                    "mr-0.5": groupedTagCount && groupedTagCount < entriesCount,
                  })}
                  onClick={() => handleFilterClick(tag)}
                >
                  <span className="max-w-28 truncate leading-normal">{tag.name}</span>
                </Badge>
              )}
              {groupedTagCount && groupedTagCount < entriesCount && (
                <span className="text-xs mr-2">x{groupedTagCount}</span>
              )}
            </div>
          )
        })}
        {tags.length > tagCount && (
          <Badge variant="tag" className="max-h-[22px]">
            <p>
              {remainingCount > MAX_DISPLAYED_REMAINING ? ">" : "+"}
              {Math.min(remainingCount, MAX_DISPLAYED_REMAINING)}
            </p>
          </Badge>
        )}
      </div>

      <ConfirmationDialog
        title={`Remove tag '${selectedTag?.name}'`}
        description={`Are you sure you want to remove the tag '${selectedTag?.name}' affecting ${selectedTagSkuIds.length} SKUs?`}
        onConfirm={() => {
          if (selectedTag) {
            onRemoveTag(selectedTag, selectedTagSkuIds, selectedTagEntries.length)
          }
        }}
        onCancel={() => setSelectedTag(undefined)}
        open={!!selectedTag?.id}
        disabled={!canEdit}
      />
    </>
  )
}
